import * as THREE from 'three';
import { PICK_MATERIAL } from '../core/materials.js';
import { VoxelGrid } from '../core/voxel-grid.js';
import { createVoxelMaterial } from '../core/materials.js';
import { mulberry32 } from '../core/rng.js';
import { stampHouse } from './props.js';

// ---------------------------------------------------------------------------
// A house that happens to be a link.
//
// The village houses are stamped straight into the world grid, which makes
// them cheap but anonymous — you cannot tint one on hover when it shares a
// mesh with 400,000 other voxels. A linked house is therefore built on its own
// little grid, exactly like a windmill is, so it gets its own material and can
// light up when the pointer finds it.
// ---------------------------------------------------------------------------

const BASE = 2;      // voxel y the house sits on inside its private grid

export function createLinkHouse({ env, palette, voxelSize, propBlocks }, cfg) {
    const P = propBlocks;
    const rng = mulberry32(cfg.id.length * 7919 + 13);

    const w = cfg.width ?? 9;
    const d = cfg.depth ?? 7;
    const wallHeight = cfg.wallHeight ?? 5;

    // Room for the roof overhang on every side.
    const W = w + 4;
    const D = d + 4;
    const grid = new VoxelGrid(W, 24, D);
    const cx = W >> 1;
    const cz = D >> 1;

    // stampHouse asks the ground how high it is; here the ground is flat by
    // construction, so a two-field stub is all the polder it needs.
    const flatGround = { top: () => BASE, seaLevel: 0 };
    stampHouse(grid, P, flatGround, cx, cz, {
        w,
        d,
        wallHeight,
        wall: P.wallBlue,
        roof: P.roofSlate,
        rng,
    });

    const material = createVoxelMaterial(env, { highlight: cfg.accent });
    const geometry = grid.build(palette, {
        voxelSize,
        // Put the doorstep at the group origin so placement matches the mills.
        origin: new THREE.Vector3(-cx * voxelSize, -BASE * voxelSize, -cz * voxelSize),
    });

    const group = new THREE.Group();
    group.add(new THREE.Mesh(geometry, material));

    // --- invisible pick volume ---
    // Generous on purpose: a house is a much smaller target than a windmill,
    // and a link nobody can hit is not a link.
    const pickH = (wallHeight + 8) * voxelSize;
    const pick = new THREE.Mesh(
        new THREE.BoxGeometry((w + 3) * voxelSize, pickH, (d + 3) * voxelSize),
        PICK_MATERIAL,
    );
    pick.position.y = pickH / 2 - voxelSize;
    pick.renderOrder = -1;
    pick.userData.site = cfg;
    group.add(pick);

    const shadow = new THREE.Mesh(
        new THREE.CircleGeometry(Math.max(w, d) * 0.8 * voxelSize, 20),
        new THREE.MeshBasicMaterial({
            color: 0x1a2417,
            transparent: true,
            opacity: 0.3,
            depthWrite: false,
        }),
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.06;
    group.add(shadow);

    let highlight = 0;

    return {
        config: cfg,
        group,
        pick,
        material,
        labelAnchor: new THREE.Vector3(0, (wallHeight + 7) * voxelSize, 0),

        update(dt, { hovered = false, focused = false } = {}) {
            // No sails to spin, so the whole house warms up instead — as if
            // someone inside turned the lamps up when a visitor approached.
            const want = hovered || focused ? 0.2 : 0;
            highlight += (want - highlight) * Math.min(1, dt * 8);
            material.uniforms.uHighlightAmount.value = highlight;
        },
    };
}
